import { useState } from "react";
import { Plus, Trash } from "@phosphor-icons/react";
import UniversalPickerModal from "shared/components/UniversalPickerModal/UniversalPickerModal";

export interface BrokerContact {
  id: number;
  firstName: string;
  lastName: string;
  email?: string;
  phone_mobile?: string;
  company?: { id?: number; name?: string } | null;
}

export interface BrokerListCellProps {
  brokers: BrokerContact[] | null | undefined;
  onChange: (next: BrokerContact[]) => void;
  emptyLabel?: string;
  pickerTitle?: string;
}

export const BrokerListCell = ({
  brokers,
  onChange,
  emptyLabel = "Geen makelaar toegewezen",
  pickerTitle = "Selecteer makelaar",
}: BrokerListCellProps) => {
  const [isPickerOpen, setIsPickerOpen] = useState(false);
  const list = brokers ?? [];

  const handleAdd = (selected: any) => {
    if (!selected || !selected.id) {
      setIsPickerOpen(false);
      return;
    }
    const id = Number(selected.id);
    if (list.some((b) => Number(b.id) === id)) {
      setIsPickerOpen(false);
      return;
    }
    const next: BrokerContact = {
      id,
      firstName: selected.firstName || "",
      lastName: selected.lastName || "",
      email: selected.email || undefined,
      phone_mobile:
        selected.phone_mobile || selected.phoneMobile || undefined,
      company: selected.company
        ? {
            id: selected.company.id,
            name: selected.company.name,
          }
        : null,
    };
    onChange([...list, next]);
    setIsPickerOpen(false);
  };

  const handleRemove = (brokerId: number) => {
    onChange(list.filter((b) => Number(b.id) !== brokerId));
  };

  return (
    <>
      <div className="flex flex-col gap-1">
        {list.length === 0 ? (
          <span className="text-sm italic text-neutral-primary/60">{emptyLabel}</span>
        ) : (
          list.map((broker) => {
            const fullName =
              `${broker.firstName || ""} ${broker.lastName || ""}`.trim() ||
              broker.email ||
              "Onbekend";
            const companyName = broker.company?.name?.trim();
            const companyId = broker.company?.id;
            return (
              <div
                key={broker.id}
                className="group flex items-center justify-between gap-2 rounded-sm px-1 py-0.5 hover:bg-neutral-primary/5"
              >
                <div className="flex flex-col text-sm">
                  <a
                    href={`/contact/${broker.id}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-neutral-primary hover:text-moss-primary hover:underline"
                    title="Open contact"
                  >
                    {fullName}
                  </a>
                  {companyName && (
                    companyId ? (
                      <a
                        href={`/company/${companyId}`}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-xs text-neutral-secondary-mid hover:text-moss-primary hover:underline"
                        title="Open bedrijf"
                      >
                        {companyName}
                      </a>
                    ) : (
                      <span className="text-xs text-neutral-secondary-mid">
                        {companyName}
                      </span>
                    )
                  )}
                </div>
                <button
                  onClick={() => handleRemove(broker.id)}
                  className="p-1 text-red-500 opacity-0 transition-opacity hover:text-red-600 group-hover:opacity-100 shrink-0"
                  title="Ontkoppelen"
                >
                  <Trash size={14} />
                </button>
              </div>
            );
          })
        )}
        <button
          onClick={() => setIsPickerOpen(true)}
          className="mt-0.5 flex items-center gap-1 self-start rounded-sm px-1 text-xs font-medium text-moss-primary hover:bg-moss-primary/10"
        >
          <Plus size={12} weight="bold" />
          Makelaar toevoegen
        </button>
      </div>

      <UniversalPickerModal
        isOpen={isPickerOpen}
        mode="reporter"
        title={pickerTitle}
        onClose={() => setIsPickerOpen(false)}
        onConfirm={handleAdd}
        config={{
          searchPlaceholder: "Zoek op naam, bedrijf of tag...",
        }}
      />
    </>
  );
};

export default BrokerListCell;
